import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a Date to API datetime string: YYYY-MM-DDTHH:mm:ss with time always 00:00:00.
 * Use for reports/ride-history "from" query param (start of day).
 */
export function formatDateToApiDateTime(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00`;
}

/**
 * Formats a Date to API datetime string with actual time: YYYY-MM-DDTHH:mm:ss.
 * Use for reports/ride-history "to" query param so the range includes the full day or current moment.
 */
export function formatDateTimeToApiString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

/**
 * Formats a Date object to YYYY-MM-DD string in local timezone
 * This prevents timezone offset issues when storing dates
 * @param date - Date object to format
 * @returns Formatted date string (YYYY-MM-DD)
 */
export function formatDateToLocalString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parses various date formats (string, Date, number[], number) into a Date object.
 * Handles Java LocalDateTime arrays like [2026, 3, 13, 10, 30, 0] and epoch millis.
 */
function parseDate(value: any): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') return new Date(value);
  if (typeof value === 'number') return new Date(value);
  if (Array.isArray(value) && value.length >= 3) {
    // Java LocalDateTime array: [year, month, day, hour?, minute?, second?]
    const [y, m, d, h = 0, min = 0, s = 0] = value;
    return new Date(y, m - 1, d, h, min, s);
  }
  return null;
}

/**
 * Formats a date string or Date object to DD/MM/YYYY format
 * @param dateString - Date string, Date object, epoch number, or Java date array
 * @returns Formatted date string (DD/MM/YYYY)
 */
export function formatDisplayDate(dateString?: any): string {
  const date = parseDate(dateString);
  if (!date || isNaN(date.getTime())) return 'N/A';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Formats a date string or Date object to DD/MM/YYYY HH:MM format
 * @param dateString - Date string, Date object, epoch number, or Java date array
 * @returns Formatted date and time string (DD/MM/YYYY HH:MM)
 */
export function formatDisplayDateTime(dateString?: any): string {
  const date = parseDate(dateString);
  if (!date || isNaN(date.getTime())) return 'N/A';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}